import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';


// ─── Single scrolling column ──────────────────────────────────────────────────

/**
 * GalleryScrollingColumn
 * Infinite auto-scrolling image column. The image list is doubled and the column
 * animates to -50% translateY, then loops seamlessly.
 *
 * Props:
 *   images        — array of { id, src, title, originalIndex }
 *   duration      — scroll speed in seconds (higher = slower)
 *   onImageClick  — called with originalIndex when an image is clicked
 *   reverse       — scroll upward instead of downward (default false)
 */
const GalleryScrollingColumn = ({ images, duration = 20, onImageClick, reverse = false }) => {
    if (!images?.length) return null;

    return (
        <div className="overflow-hidden flex-1 min-w-0">
            <motion.div
                animate={{ translateY: reverse ? '0%' : '-50%' }}
                initial={{ translateY: reverse ? '-50%' : '0%' }}
                transition={{
                    duration,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatType: 'loop',
                }}
                className="flex flex-col gap-3"
            >
                {[0, 1].map((pass) => (
                    <React.Fragment key={pass}>
                        {images.map((img, i) => (
                            <button
                                key={`${pass}-${i}`}
                                onClick={() => onImageClick?.(img.originalIndex)}
                                className="group relative block w-full overflow-hidden rounded-2xl bg-gray-100 cursor-zoom-in focus:outline-none"
                            >
                                <img
                                    src={img.src}
                                    alt={img.title || ''}
                                    loading="lazy"
                                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-primary-950/0 group-hover:bg-primary-950/30 transition-all duration-300 flex items-center justify-center">
                                    <svg
                                        className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                    </svg>
                                </div>
                            </button>
                        ))}
                    </React.Fragment>
                ))}
            </motion.div>
        </div>
    );
};

// ─── Column speeds / direction config ─────────────────────────────────────────

const COLUMN_CONFIGS = [
    { duration: 22, reverse: false },
    { duration: 17, reverse: true  },
    { duration: 26, reverse: false },
    { duration: 19, reverse: true  },
];

// ─── Responsive column count ──────────────────────────────────────────────────
// mobile (<640): 1 column — tablet (640–1023): 2 columns — desktop (≥1024): up to maxColumns

const getColCount = (maxColumns) => {
    const w = window.innerWidth;
    if (w < 640)  return 1;
    if (w < 1024) return Math.min(2, maxColumns);
    return Math.min(maxColumns, COLUMN_CONFIGS.length);
};

const useResponsiveColumns = (maxColumns) => {
    const [colCount, setColCount] = useState(() => getColCount(maxColumns));
    useEffect(() => {
        const update = () => setColCount(getColCount(maxColumns));
        window.addEventListener('resize', update, { passive: true });
        return () => window.removeEventListener('resize', update);
    }, [maxColumns]);
    return colCount;
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * GalleryScrollingColumns
 * Splits images evenly across N columns, each scrolling at a different speed.
 * Odd columns scroll down, even columns scroll up — creating a parallax depth effect.
 *
 * Props:
 *   images        — array of { id, src, title } (originalIndex is set internally)
 *   columns       — number of columns (default 3, max 4)
 *   height        — container height class (default "h-[80vh]")
 *   onImageClick  — called with original image index when an image is clicked
 */
const GalleryScrollingColumns = ({ images = [], columns = 3, height = 'h-[80vh]', onImageClick }) => {
    const colCount = useResponsiveColumns(columns);

    // Tag each image with its original index before splitting
    const tagged = images.map((img, i) => ({ ...img, originalIndex: i }));

    // Distribute images evenly across columns
    const cols = Array.from({ length: colCount }, (_, ci) =>
        tagged.filter((_, i) => i % colCount === ci)
    );

    return (
        <div className={`flex gap-3 ${height} overflow-hidden`}>
            {cols.map((colImages, ci) => (
                <GalleryScrollingColumn
                    key={ci}
                    images={colImages}
                    duration={COLUMN_CONFIGS[ci].duration}
                    reverse={COLUMN_CONFIGS[ci].reverse}
                    onImageClick={onImageClick}
                />
            ))}
        </div>
    );
};

export default GalleryScrollingColumns;
